import { CloudDownloadOutlined, FormOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Avatar, message, Button, Card, Col, List, Space, Switch, Typography, Badge, Modal, Row, Descriptions, Grid, Table, Statistic, Tag } from 'antd';
import { Content } from 'antd/es/layout/layout';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../App';
import { AddClinicalTrialPopup, ClinicalTrialPopup } from '../components/ClinicalTrialPopup';
import { PatientAdmissionsTable } from '../components/PatientAdmission';
import { PatientsTable } from '../components/Patients';
import { Ecrf, Inquiry, Patient, PatientAdmission, UserInfo } from '../models/models';
import { deletePatient, getECRFS, getMyInquiries, getPatientAdmission, getPatients, postPatient, postPatientAdmission } from '../models/serverApi';
import EcrfList from '../components/EcrfList';
import dayjs from 'dayjs';

const Dashboard: React.FC = props => {
    const [context, setContext] = useContext(AppContext);
    const [ready, setReady] = useState(false);
    const [users, setUsers] = useState<Array<UserInfo>>([]);
    const [patientAdmissions, setPatientAdmissions] = useState<Array<PatientAdmission>>([]);
    const [patients, setPatients] = useState<Array<Patient>>([]);
    const [pendingInquiries, setPendingInquiries] = useState<Inquiry[]>([]);

    const navigate = useNavigate();
    const screens = Grid.useBreakpoint();

    useEffect(() => {
        if (ready) {
            return;
        }

        getPatientAdmission().then((pa) => {
            setPatientAdmissions(pa);
            setReady(true);
        });

        getPatients().then((pa) => {
            setPatients(pa);
            setReady(true);
        });

        getMyInquiries().then(setPendingInquiries);

        setContext(prevContext => {
            return {
                ...prevContext,
                selection: {
                    clinicalTrial: prevContext.selection?.clinicalTrial,
                    patient: undefined,
                    admission: undefined,
                }
            };
        });
    }, [ready]);

    const isAdmin = () => {
        return context.user?.admin;
    }

    const patientAdmissionFinish = async (patientAdmission: PatientAdmission) => {
        setReady(false);
    }

    const patientAdmissionDeleteFinish = (patientAdmission: PatientAdmission) => {
        setReady(false);
    }

    const patientsFinish = async (patient?: Patient) => {
        setReady(false);
    }

    const getFilesSource = () => {
        if (!context.selection?.clinicalTrial) {
            return [];
        }

        return [{
            name: "Πρωτόκολλο μελέτης",
            file: context.selection?.clinicalTrial.protocolFile,
        },
        {
            name: "Φόρμα συγκατάθεσης ασθενούς",
            file: context.selection?.clinicalTrial.patientConsentFile
        }];
    }

    const handleInquiryClick = async (inquiry: Inquiry) => {
        const [pts, admissions] = await Promise.all([getPatients(), getPatientAdmission()]);
        const patient = pts.find(p => p.id === inquiry.patientId);
        const admission = admissions.find(a => a.id === inquiry.patientAdmissionId);
        setContext(prev => ({
            ...prev,
            selection: { ...prev.selection, patient, admission } as any
        }));
        navigate(
            `/ecrf/${inquiry.ecrfId}?sectionId=${inquiry.sectionId}&fieldId=${inquiry.fieldId}`,
            { state: { patientId: inquiry.patientId, admissionId: inquiry.patientAdmissionId } }
        );
    };

    return (<>
        <Badge.Ribbon text={context.user?.admin ? "Admin" : context.user?.professor ? "Professor" : "User"}>
            <Content>
                <Row>
                    <Col span={24}>
                        {!isAdmin &&
                            <Card title={`${context?.selection?.clinicalTrial?.title}`}
                                styles={{ body: { padding: "0" } }} bordered hoverable className="always" style={{ marginTop: "24px" }}>
                            </Card>
                        }
                    </Col>
                    <Col span={24}>
                        <Card title="Υλικό μελέτης" bordered hoverable className="always" style={{ marginTop: "24px" }}>
                            <List
                                dataSource={getFilesSource()}
                                renderItem={(item) => (
                                    <List.Item actions={
                                        [<Button type="primary" onClick={() => {
                                            window.open(`/Uploads/${item.file}`, "_blank");
                                        }}>
                                            <CloudDownloadOutlined /></Button>]
                                    }>{item.name}
                                    </List.Item>
                                )}
                            />
                        </Card>
                    </Col>

                    <Col span={24}>
                        <Card
                            title={
                                <span>
                                    <QuestionCircleOutlined style={{ color: "#faad14", marginRight: 8 }} />
                                    Εκκρεμείς Ερωτήσεις {pendingInquiries.length > 0 && `(${pendingInquiries.length})`}
                                </span>
                            }
                            bordered
                            hoverable
                            style={{ marginTop: "24px" }}
                        >
                            {pendingInquiries.length === 0 ? (
                                <Typography.Text type="secondary">Δεν υπάρχουν εκκρεμείς ερωτήσεις.</Typography.Text>
                            ) : (
                                <List
                                    dataSource={pendingInquiries}
                                    renderItem={(item) => (
                                        <List.Item
                                            actions={[
                                                <Button type="primary" onClick={() => handleInquiryClick(item)}>
                                                    Μετάβαση στο πεδίο
                                                </Button>
                                            ]}
                                        >
                                            <List.Item.Meta
                                                title={
                                                    <span>
                                                        Πεδίο: <strong>{item.fieldLabel ?? `#${item.fieldId}`}</strong>
                                                        {" — "}
                                                        Ασθενής: <strong>{item.patientName ?? `#${item.patientId}`}</strong>
                                                    </span>
                                                }
                                                description={
                                                    <span>
                                                        Από: {item.fromUserName}
                                                        {item.sectionName && ` | Ενότητα: ${item.sectionName}`}
                                                        {item.note && ` | Σημείωση: "${item.note}"`}
                                                        {item.fieldValueSnapshot && ` | Τιμή: ${item.fieldValueSnapshot}`}
                                                        {` | ${dayjs(item.createdAt).format("DD/MM/YYYY HH:mm")}`}
                                                    </span>
                                                }
                                            />
                                        </List.Item>
                                    )}
                                />
                            )}
                        </Card>
                    </Col>

                    <Col span={24}>
                        <PatientsTable patients={patients} patientAdmissions={patientAdmissions} afterEdit={patientsFinish} afterDelete={patientsFinish} ></PatientsTable>
                    </Col>
                </Row>
                <Card bordered hoverable className="always" style={{ marginTop: "50px" }}>
                    &copy; 2026 - <a href="https://www.ede.gr/">Ελληνική Διαβητολογική Εταιρεία</a> | Powered by <a href="https://biostats.gr/e-crf-edc/">biostats.gr</a>
                </Card>
            </Content>
        </Badge.Ribbon>
    </>);
}

export default Dashboard;
